import type { AgentAccent, SharingRequest } from '@ralph/contracts'
import clsx from 'clsx'
import { ArrowRight, BookOpen, Check, ChevronDown, Globe, Lock, TriangleAlert, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiRequestError } from '@/api/client.ts'
import { GroupChip } from '@/components/groups/GroupLabel.tsx'
import { AgentAvatar } from '@/components/marketplace/consumer/AgentAvatar.tsx'
import { decisionOutcome } from '@/components/sharing/sharing-outcome.ts'
import { Notice } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, EmptyState, Panel, Spinner, Textarea } from '@/components/ui/primitives.tsx'
import { useDecideSharingRequest, useSharingQueue, useSharingRequest } from '@/hooks/use-sharing.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'

/**
 * Governance → Sharing requests (PRD-26 D-29, GRP-080).
 *
 * One card per request, oldest first, carrying everything a decision needs and nothing a person
 * would have to go and look up: what it is, who asked and when, who could use it now and who
 * would if approved (in people, counted once), why it is here at all, what those people would
 * get — the agent's disclosure for the version that would go live, or the base's contents — and
 * what changed since it was asked. A request is decided whole (D-15); a decline says why.
 *
 * The decision is bound to the digest on the card the administrator is reading (D-49). If the
 * item changed underneath — renamed, republished, narrowed — the server refuses, the queue
 * reloads, and the card says to read it again. Nothing is re-bound to what is there now.
 */

type Props = { selectedId: string | null }

/** A card's title, addressable from the queue so focus can land on the card that follows a decision. */
const cardTitleId = (requestId: string): string => `sharing-request-${requestId}-title`

export const SharingQueue = ({ selectedId }: Props) => {
  const fmt = useI18n()
  const t = fmt.t.sharing
  const [history, setHistory] = useState(false)
  const queue = useSharingQueue(history ? 'all' : 'pending', true)
  const items = queue.data?.items ?? []

  // What the last decision did, said in words: the card leaves the list either way, and a request
  // the server closed instead of approving must not read as shared (GRP-080).
  const [outcome, setOutcome] = useState<string | null>(null)
  const outcomeRef = useRef<HTMLParagraphElement>(null)
  const [refocusAt, setRefocusAt] = useState<number | null>(null)

  // A notification can name a request that is no longer waiting — replaced by a newer one, withdrawn,
  // or decided by another administrator. It is read on its own and said in words, rather than
  // opening the queue on nothing in particular.
  const selectedGone = selectedId !== null && !queue.isLoading && !items.some((item) => item.id === selectedId)
  const gone = useSharingRequest(selectedGone ? selectedId : null)

  // Once the list has been read again, focus goes to the card that took the decided one's place,
  // or to the outcome when none did — never to the page body.
  useEffect(() => {
    if (refocusAt === null || queue.isFetching) return
    const next = items[refocusAt]
    const target = next ? document.getElementById(cardTitleId(next.id)) : outcomeRef.current
    target?.focus()
    setRefocusAt(null)
  }, [refocusAt, items, queue.isFetching])

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <p className="min-w-0 flex-1 basis-[16rem] text-pretty text-[13px] leading-[1.5] text-fg-muted">{t.lead}</p>
        <button
          type="button"
          aria-pressed={history}
          onClick={() => setHistory((current) => !current)}
          className="text-[12.5px] text-fg-muted underline-offset-2 hover:text-fg hover:underline"
        >
          {history ? t.hideHistory : t.showHistory}
        </button>
      </div>

      {queue.isLoading && (
        <Panel role="status" className="flex items-center gap-2 px-4 py-6 text-[13px] text-fg-subtle">
          <Spinner /> {t.loading}
        </Panel>
      )}
      {queue.isError && (
        <Panel className="px-4">
          <EmptyState title={t.failed} body={t.failedBody} />
        </Panel>
      )}
      {!queue.isLoading && !queue.isError && items.length === 0 && (
        <Panel className="px-4">
          <EmptyState title={t.empty} body={t.emptyBody} />
        </Panel>
      )}

      {selectedGone && gone.data && (
        <p className="rounded-tile border border-rule bg-card-solid/55 px-3.5 py-2.5 text-pretty text-[13px] leading-[1.5] text-fg-muted">
          {t.notWaiting(gone.data)}
        </p>
      )}

      {/* Mounted empty, so the outcome of a decision is announced the moment it lands. */}
      <div role="status" className="text-[13px] leading-[1.5]">
        {outcome && (
          <p
            ref={outcomeRef}
            tabIndex={-1}
            className="rounded-tile border border-rule bg-card-solid/55 px-3.5 py-2.5 focus:outline-none"
          >
            {outcome}
          </p>
        )}
      </div>

      <ul className="space-y-3">
        {items.map((request, index) => (
          <li key={request.id} className="rise" style={{ animationDelay: `${Math.min(index, 7) * 45}ms` }}>
            <RequestCard
              request={request}
              selected={request.id === selectedId}
              onDecided={(result) => {
                setOutcome(decisionOutcome(fmt, result))
                setRefocusAt(index)
              }}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

const RequestCard = ({
  request,
  selected,
  onDecided
}: {
  request: SharingRequest
  selected: boolean
  onDecided: (result: SharingRequest) => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.sharing
  const headingId = cardTitleId(request.id)
  const noteId = useId()
  const ref = useRef<HTMLElement>(null)
  const decide = useDecideSharingRequest()
  const [note, setNote] = useState('')
  const [showInstructions, setShowInstructions] = useState(false)
  const pending = request.status === 'pending'

  // A notification that lands on a request brings it into view, once.
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [selected])

  const stale = decide.error instanceof ApiRequestError && decide.error.code === 'approval_digest_mismatch'
  const decided = decide.error instanceof ApiRequestError && decide.error.code === 'precondition_failed'
  // Through the catalogue, not through `issues[0].message`. Laundering the server's English
  // into a local variable put it past the LOC-033 gate — which greps for a variable called
  // `error` — while still rendering English on a Portuguese screen (PRD-27 D-4).
  const otherError = decide.isError && !stale && !decided ? describeApiError(fmt.t, decide.error) : null

  const disclosure = request.disclosure as AgentProjection
  // Deduped, because two fields this client has no word for both read as "something else" and a
  // list that says it twice reads as a bug rather than as discretion.
  const changed = [...new Set(request.changedSince.map((entry) => t.fieldNames[entry.field] ?? t.fieldUnknown))]

  const submit = (decision: 'approved' | 'declined') =>
    decide.mutate({ id: request.id, decision, note: note.trim(), digest: request.digest }, { onSuccess: onDecided })

  return (
    <article
      ref={ref}
      aria-labelledby={headingId}
      className={clsx(
        'overflow-hidden rounded-card border bg-card shadow-glass',
        selected ? 'border-primary' : 'border-rule'
      )}
    >
      <header className="flex flex-wrap items-start gap-3 border-b border-rule px-4 py-3.5">
        {request.resourceKind === 'agent' ? (
          <AgentAvatar
            displayName={request.resourceName}
            icon={request.resourceIcon ?? ''}
            accent={(request.resourceAccent ?? 'slate') as AgentAccent}
          />
        ) : (
          <span
            aria-hidden
            className="flex h-[37px] w-[37px] shrink-0 items-center justify-center rounded-tile border border-rule bg-card-solid text-fg-muted"
          >
            <BookOpen size={16} />
          </span>
        )}
        {/* A basis, so on a phone the status wraps below rather than squeezing the name: the
            reviewer is deciding about this thing, so its whole name shows. */}
        <div className="min-w-0 flex-1 basis-[12rem]">
          <p className="eyebrow">{t.kinds[request.resourceKind]}</p>
          <h3
            id={headingId}
            tabIndex={-1}
            className="break-words text-[15px] font-semibold tracking-[-0.018em] focus:outline-none"
          >
            {request.resourceName}
          </h3>
          <p className="readout mt-0.5 text-fg-subtle">
            {t.asked(request.requestedBy?.displayName ?? null, fmt.formatAgo(request.requestedAt))}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 sm:flex-col sm:items-end">
          <Badge tone={pending ? 'primary' : request.status === 'approved' ? 'grounded' : 'neutral'}>
            {t.status[request.status]}
          </Badge>
          <Link
            to={request.resourceKind === 'agent' ? `/agents/${request.resourceId}` : `/knowledge/${request.resourceId}`}
            className="inline-flex items-center gap-1 text-[12px] text-fg-muted hover:text-fg"
          >
            {t.open[request.resourceKind]}
            <ArrowRight size={11} aria-hidden />
          </Link>
        </div>
      </header>

      <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
        <section>
          <h4 className="eyebrow mb-1.5">{t.wantsToShare}</h4>
          {request.requested.kind === 'everyone' ? (
            <p className="inline-flex items-center gap-1.5 text-[13px] font-semibold">
              <Globe size={13} aria-hidden className="text-primary" />
              {t.everyone}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {request.requested.groups.map((group) => (
                <li key={group.id}>
                  <GroupChip
                    group={group}
                    mark={{ text: `${fmt.formatCount(group.memberCount)} · ${t.added}`, tone: 'primary' }}
                  />
                </li>
              ))}
            </ul>
          )}

          <h4 className="eyebrow mb-1.5 mt-4">{t.now}</h4>
          {request.live.kind === 'everyone' ? (
            <p className="inline-flex items-center gap-1.5 text-[13px]">
              <Globe size={13} aria-hidden className="text-fg-subtle" />
              {t.nowEveryone}
            </p>
          ) : request.live.kind === 'private' || request.live.groups.length === 0 ? (
            <p className="inline-flex items-center gap-1.5 text-[13px]">
              <Lock size={13} aria-hidden className="text-fg-subtle" />
              {t.nowPrivate}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {request.live.groups.map((group) => (
                <li key={group.id}>
                  <GroupChip group={group} mark={{ text: fmt.formatCount(group.memberCount), tone: 'neutral' }} />
                </li>
              ))}
            </ul>
          )}

          <p className="mt-3 text-[13px] leading-[1.5]">{t.reach(fmt, request.reach.now, request.reach.ifApproved)}</p>
          <p className="mt-1 text-[13px] leading-[1.5] text-fg-muted">
            {request.reason === 'everyone'
              ? t.why.everyone
              : t.why.unmanaged_groups(
                  fmt,
                  request.requestedBy?.displayName ?? null,
                  request.requested.groups.map((group) => group.name)
                )}
          </p>
        </section>

        <section>
          <h4 className="eyebrow mb-1.5">{t.what}</h4>
          {request.resourceKind === 'agent' && request.version && (
            <div className="space-y-2 text-[13px] leading-[1.5]">
              <p>{t.version(request.version.wouldPublish, request.version.requested, request.version.live)}</p>
              {changed.length > 0 && <p className="text-alert">{t.changedSince(fmt, changed)}</p>}
              <AgentDisclosure
                disclosure={disclosure}
                showInstructions={showInstructions}
                onToggleInstructions={() => setShowInstructions((current) => !current)}
              />
            </div>
          )}
          {request.resourceKind === 'knowledge_base' && request.base && (
            <div className="space-y-2 text-[13px] leading-[1.5]">
              <p>{t.base(fmt, request.base.documentCount, request.base.connectedFolders)}</p>
              {request.base.sampleTitles.length > 0 && (
                <>
                  <p className="eyebrow mt-2">{t.sampleTitles}</p>
                  <ul className="list-inside list-disc text-fg-muted">
                    {request.base.sampleTitles.map((title) => (
                      <li key={title} className="truncate">
                        {title}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
          <p className="mt-3 text-[12.5px] leading-[1.5] text-fg-muted">{t.coversLater[request.resourceKind]}</p>
        </section>
      </div>

      {request.ackText !== '' && (
        <div className="border-t border-rule px-4 py-3">
          <p className="eyebrow mb-1">{t.acknowledged}</p>
          <blockquote className="rounded-tile bg-fg/[0.03] px-3 py-2 text-[12.5px] italic leading-[1.55] text-fg-muted">
            “{request.ackText}”
          </blockquote>
        </div>
      )}

      {pending ? (
        <div className="border-t border-rule bg-card-solid/60 px-4 py-3.5">
          {stale && <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={t.stale} />}
          {decided && <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={t.alreadyDecided} />}
          {otherError !== null && (
            <Notice role="alert" icon={<TriangleAlert size={13} aria-hidden />} title={otherError || t.failed} />
          )}
          <label htmlFor={noteId} className="mb-1 mt-2 block text-[12.5px] font-semibold">
            {t.note}
          </label>
          <Textarea
            id={noteId}
            rows={2}
            value={note}
            maxLength={2000}
            aria-describedby={`${noteId}-hint`}
            onChange={(event) => setNote(event.target.value)}
          />
          <p id={`${noteId}-hint`} className="mt-1 text-[12px] text-fg-subtle">
            {t.noteHint}
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="secondary"
              icon={<X size={13} aria-hidden />}
              disabled={decide.isPending || note.trim() === ''}
              onClick={() => submit('declined')}
            >
              {decide.isPending ? t.deciding : t.decline}
            </Button>
            <Button
              variant="primary"
              icon={<Check size={13} aria-hidden />}
              disabled={decide.isPending}
              onClick={() => submit('approved')}
            >
              {decide.isPending ? t.deciding : t.approve}
            </Button>
          </div>
        </div>
      ) : (
        <div className="border-t border-rule px-4 py-3 text-[12.5px] text-fg-muted">
          <p>
            {t.status[request.status]}
            {request.closedReason ? ` — ${t.closedReason[request.closedReason]}` : ''}
            {request.decidedAt
              ? ` · ${t.decidedBy(request.decidedBy?.displayName ?? null, fmt.formatAgo(request.decidedAt))}`
              : ''}
          </p>
          {request.decisionNote !== '' && <p className="mt-1 italic">“{request.decisionNote}”</p>}
        </div>
      )}
    </article>
  )
}

/** The agent's disclosure as a reviewer reads it (the projection the server stored or computed). */
type AgentProjection = {
  knowledgeMode?: 'attached' | 'all'
  knowledge?: Array<{ id: string; name: string; owner?: { kind: string; name?: string; group?: { name: string } } }>
  capabilities?: Array<{ id: string; name: string; description: string; wording: 'reads' | 'acts' }>
  grounded?: boolean
  memory?: { usesSavedPreferences?: boolean; learnsHere?: boolean }
  tagline?: string
  instructions?: string
}

const AgentDisclosure = ({
  disclosure,
  showInstructions,
  onToggleInstructions
}: {
  disclosure: AgentProjection
  showInstructions: boolean
  onToggleInstructions: () => void
}) => {
  const t = useI18n().t.sharing
  const knowledge = disclosure.knowledge ?? []
  const capabilities = disclosure.capabilities ?? []
  return (
    <div className="space-y-2">
      {disclosure.tagline && <p className="text-fg-muted">{disclosure.tagline}</p>}
      <div>
        <p className="eyebrow mt-2">{t.knowledge}</p>
        {disclosure.knowledgeMode === 'all' || knowledge.length === 0 ? (
          <p className="text-fg-muted">{t.knowledgeAll}</p>
        ) : (
          <ul className="text-fg-muted">
            {knowledge.map((base) => (
              <li key={base.id} className="truncate">
                <span className="font-medium text-fg">{base.name}</span>
                {base.owner &&
                  ` · ${base.owner.kind === 'group' ? (base.owner.group?.name ?? '') : (base.owner.name ?? '')}`}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <p className="eyebrow mt-2">{t.abilities}</p>
        {capabilities.length === 0 ? (
          <p className="text-fg-muted">{t.noAbilities}</p>
        ) : (
          <ul className="space-y-0.5">
            {capabilities.map((capability) => (
              <li key={capability.id} className="flex items-baseline gap-2">
                <Badge tone={capability.wording === 'acts' ? 'alert' : 'neutral'}>
                  {capability.wording === 'acts' ? t.acts : t.reads}
                </Badge>
                <span>{capability.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-fg-muted">
        {disclosure.grounded ? t.grounded : t.notGrounded}{' '}
        {disclosure.memory?.learnsHere ? t.memoryLearns : t.memoryDoesNot}
      </p>
      {disclosure.instructions && (
        <div>
          <button
            type="button"
            aria-expanded={showInstructions}
            onClick={onToggleInstructions}
            className="inline-flex items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg"
          >
            <ChevronDown
              size={12}
              aria-hidden
              className={clsx('transition-transform', showInstructions && 'rotate-180')}
            />
            {showInstructions ? t.hideInstructions : t.showInstructions}
          </button>
          {showInstructions && (
            <pre className="mt-1.5 max-h-[16rem] overflow-y-auto whitespace-pre-wrap rounded-tile bg-fg/[0.03] px-3 py-2 font-mono text-[11.5px] leading-[1.55] text-fg-muted">
              {disclosure.instructions}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}
